/**
 * PremiumSaleBanner - Black Friday banner: Premium monthly half off for 3 months, and the
 * PMBOK 8 Exam Prep course half off (one-time). Premium members only see the course part.
 * Shows only during the sale window (src/config/premiumSale.js), or for admins with
 * ?previewSale=1. Hidden for people who already have Premium. Client-side only, so
 * SEO shells and page meta never change. Follows the site light/dark switch.
 *
 * variant="site"      slim bar under the public header (HomePageLayout)
 * variant="dashboard" card at the top of the learner dashboard area (Free members)
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Tag, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import usePremiumSale from "../../hooks/usePremiumSale";
import {
  PREMIUM_SALE,
  SALE_TERMS_TEXT,
  SALE_ENDS_TEXT,
  SALE_STARTS_TEXT,
  COURSE_SALE_PRICE_TEXT,
  COURSE_REGULAR_PRICE_TEXT,
  timeLeft,
} from "../../config/premiumSale";

const DISMISS_KEY = `scope180-saleBannerClosed-${PREMIUM_SALE.id}`;

export function SaleCountdown({ now, className = "" }) {
  const t = timeLeft(now);
  const pad = (n) => String(n).padStart(2, "0");
  const text = t.days > 0
    ? `${t.days}d ${pad(t.hours)}h ${pad(t.minutes)}m ${pad(t.seconds)}s`
    : `${pad(t.hours)}h ${pad(t.minutes)}m ${pad(t.seconds)}s`;
  return (
    <span className={`tabular-nums whitespace-nowrap ${className}`} aria-label={SALE_ENDS_TEXT}>
      Ends in {text}
    </span>
  );
}

export function SalePreviewNote({ className = "" }) {
  return (
    <span className={`inline-block rounded-full bg-slate-900/10 dark:bg-white/10 px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      Admin preview - checkout charges regular prices until the sale starts
    </span>
  );
}

export default function PremiumSaleBanner({ variant = "site" }) {
  const { subscription, isAdmin } = useAuth();
  const { phase, isPreview, now } = usePremiumSale({ tick: true });
  const [closed, setClosed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  if (phase === "none") return null;
  // Premium members can still buy the course, so they see only that part.
  const showPremium = !subscription?.isPremium || isPreview;
  if (variant === "dashboard" && isAdmin && !isPreview) return null;
  if (closed && !isPreview) return null;

  const close = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setClosed(true);
  };

  if (phase === "teaser") {
    return (
      <div className="w-full border-b border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
        <p className="mx-auto max-w-7xl px-4 py-2 text-center text-sm">
          {PREMIUM_SALE.label} sale {SALE_STARTS_TEXT}:{" "}
          {showPremium && <>Premium will be {SALE_TERMS_TEXT}, and </>}
          the PMBOK 8 Exam Prep course will be {COURSE_SALE_PRICE_TEXT} one-time (regular {COURSE_REGULAR_PRICE_TEXT}).
          {isPreview && <> <SalePreviewNote /></>}
        </p>
      </div>
    );
  }

  if (variant === "dashboard") {
    return (
      <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-slate-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-50 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              <Tag size={14} aria-hidden="true" /> {PREMIUM_SALE.label} through Cyber Monday
            </p>
            {showPremium && <p className="mt-1 text-base font-bold">Premium: {SALE_TERMS_TEXT}.</p>}
            <p className="mt-1 text-base font-bold">
              PMBOK 8 Exam Prep course (35 contact hours): {COURSE_SALE_PRICE_TEXT} one-time, regular {COURSE_REGULAR_PRICE_TEXT}.
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-amber-100/80">
              <SaleCountdown now={now} />{showPremium && " - cancel Premium anytime."}
            </p>
            {isPreview && <SalePreviewNote className="mt-2" />}
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:items-end">
            {showPremium && (
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-400"
              >
                See Premium
              </Link>
            )}
            <Link
              to="/academy/pmbok8-exam-prep"
              className="inline-flex items-center justify-center rounded-xl border border-amber-500 px-5 py-2.5 text-sm font-bold text-amber-800 transition-colors hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-500/20"
            >
              See the course
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={`${PREMIUM_SALE.label} sale`}
      className="w-full border-b border-amber-300 bg-amber-50 text-slate-900 dark:border-amber-500/30 dark:bg-[#1d1a0f] dark:text-amber-50"
    >
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 pr-10 text-center text-sm">
        <span className="font-bold text-amber-700 dark:text-amber-300">{PREMIUM_SALE.label}:</span>
        {showPremium && (
          <span>
            Premium is {SALE_TERMS_TEXT}.{" "}
            <Link to="/pricing" className="font-bold underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300">See Premium</Link>
          </span>
        )}
        <span>
          PMBOK 8 course: {COURSE_SALE_PRICE_TEXT} one-time <span className="whitespace-nowrap">(regular {COURSE_REGULAR_PRICE_TEXT})</span>.{" "}
          <Link to="/academy/pmbok8-exam-prep" className="font-bold underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300">See the course</Link>
        </span>
        <SaleCountdown now={now} className="font-semibold" />
        {isPreview && <SalePreviewNote />}
        <button
          type="button"
          onClick={close}
          aria-label="Close the sale banner"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-black/5 hover:text-slate-900 dark:text-amber-100/70 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
